import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoManagedReturnsRpcClientContext } from "@redotech/redo-merchant-managed-returns-app/rpc-client";
import { type RedoManagedLocationState } from "@redotech/redo-model/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useContext, useMemo } from "react";
import * as css from "./redo-managed-location.module.css";

export const RedoManagedLocationDetails = memo(
  function RedoManagedLocationDetails({
    state,
    setState,
  }: StepTypeDetailsProps<RedoManagedLocationState>) {
    const rpcClient = useRequiredContext(RedoManagedReturnsRpcClientContext);
    const team = useContext(TeamContext);

    const returnsCentersLoad = useLoad(
      async (signal) => {
        const result = await rpcClient.getReturnsCenters({}, { signal });
        return result.returnsCenters;
      },
      [rpcClient],
    );

    const centers = useMemo(
      () => returnsCentersLoad.value ?? [],
      [returnsCentersLoad.value],
    );
    const selectedIds = useMemo(
      () => state.returnsCenterIds ?? [],
      [state.returnsCenterIds],
    );
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const selectedCenters = useMemo(
      () =>
        selectedIds
          .map((id) => centers.find((c) => c.id === id))
          .filter((c): c is NonNullable<typeof c> => c !== undefined),
      [centers, selectedIds],
    );

    const availableToAdd = useMemo(
      () => centers.filter((c) => !selectedSet.has(c.id)),
      [centers, selectedSet],
    );

    const teamSlug = team?._id?.toString();
    const settingsRoot = teamSlug
      ? `/stores/${teamSlug}/settings/warehouse`
      : undefined;

    const addOptions: RedoListItem<string>[] = useMemo(
      () =>
        availableToAdd.map((c) => ({
          id: c.id,
          value: c.id,
          type: "text",
          text: c.name,
        })),
      [availableToAdd],
    );

    const addCenter = (id: string) =>
      setState((p) => {
        const ids = p.returnsCenterIds ?? [];
        if (ids.includes(id)) {
          return p;
        }
        return { ...p, returnsCenterIds: [...ids, id] };
      });

    const removeCenter = (id: string) =>
      setState((p) => ({
        ...p,
        returnsCenterIds: (p.returnsCenterIds ?? []).filter((x) => x !== id),
      }));

    const isCfgComplete = (
      cfg: (typeof centers)[number]["config"] | undefined,
    ) =>
      !!cfg?.shipToAddress?.street1 && cfg?.consolidationScheduleDays != null;

    return (
      <div className={css.panel}>
        <div className={css.section}>
          <div className={css.fieldHeader}>
            <label className={css.fieldLabel}>Returns centers</label>
            <span className={css.fieldCount}>
              {selectedIds.length} selected
            </span>
          </div>
          <div className={css.fieldHelp}>
            Returns are routed to the closest available center based on the
            customer&apos;s address.
          </div>

          <RedoSingleSelectDropdownInput<string>
            disabled={availableToAdd.length === 0}
            options={addOptions}
            optionSelected={({ item }) => addCenter(item.value)}
            placeholder={
              availableToAdd.length === 0
                ? "All available centers added"
                : "+ Add returns center"
            }
            selectedItem={undefined}
            size="xs"
          />

          {selectedCenters.length > 0 && (
            <div className={css.centerList}>
              {selectedCenters.map((c) => {
                const ready = isCfgComplete(c.config);
                const href = settingsRoot ? `${settingsRoot}/${c.id}` : "#";
                return (
                  <a
                    className={css.centerRow}
                    href={href}
                    key={c.id}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <div className={css.centerOptionBody}>
                      <div className={css.centerName}>{c.name}</div>
                      <div className={css.centerLoc}>
                        {[c.city, c.state, c.country]
                          .filter(Boolean)
                          .join(", ") || "No location set"}
                      </div>
                      <div className={css.centerStatus}>
                        <RedoBadge
                          color={ready ? "success" : "warning"}
                          segmentLeading={{
                            type: "icon",
                            Icon: ready ? CheckIcon : AlertCircleIcon,
                          }}
                          size="xs"
                          text={ready ? "Ready" : "Setup"}
                        />
                      </div>
                    </div>

                    <div
                      className={css.removeBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeCenter(c.id);
                      }}
                    >
                      <RedoButton
                        aria-label={`Remove ${c.name}`}
                        hierarchy="tertiary"
                        IconLeading={TrashIcon}
                        size="xs"
                        text=""
                        theme="destructive"
                      />
                    </div>

                    <LinkExternalIcon
                      className={css.externalIcon}
                      height="14"
                      width="14"
                    />
                  </a>
                );
              })}
            </div>
          )}

          {selectedCenters.length === 0 && centers.length > 0 && (
            <div className={css.empty}>
              No centers added yet — pick one from the dropdown above.
            </div>
          )}

          {centers.length === 0 && !returnsCentersLoad.pending && (
            <div className={css.empty}>
              No returns centers are available to your team yet.
            </div>
          )}
        </div>
      </div>
    );
  },
);
